import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, effect, inject } from '@angular/core';
import { Status } from '@df/core/status';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { UiComponent } from '@df/ui/ui.component';
import { NCmsBranchCollection } from 'projects/app-default/src/app/branch/collection/branch-collection.component';
import { BehaviorSubject } from 'rxjs';
import { Product } from '../../catalog/product/product.decorator';
import { LoaderComponent } from '../../common/loader/loader.component';
import { SharedModule } from '../../shared.module';
import { BranchService } from '../branch.service';

type TState = 'nobranch' | 'error' | 'busy' | 'none' | 'instock' | 'limitedstock' | 'outofstock';

@Component({
    selector: 'product-collection-state',
    templateUrl: 'product-collection-state.html',
    styleUrls: ['product-collection-state.scss'],
    standalone: true,
    imports: [LoaderComponent, SharedModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductCollectionStateComponent extends UiComponent {
    /**
     * Product
     */
    @Input({
        required: true
    })
    product!: Product;

    /**
     * Requested qty
     */
    @Input()
    qty = 1;

    @Input()
    reducedSize = false;

    /**
     * Component display variant (depends on context)
     */
    @Input({
        required: true
    })
    variant!: 'PLP' | 'PDP' | 'basket';

    @Input()
    googleShoppingLayout = false;

    /**
     * Submission with given branch number
     */
    @Output()
    onsubmit = new EventEmitter<{ branchNumber: number; status: Status }>();

    /**
     * If method is availble
     */
    @Output()
    available = new BehaviorSubject<boolean | 'noselection'>(false);

    /**
     * Component state
     */
    protected state: TState = 'busy';
    /**
     * Error message
     */
    protected error?: string;

    /**
     * Selected branch number
     */
    private branchNumber: number | undefined;

    /**
     * In stock qty if store is loaded
     */
    protected stockQty?: number;

    /**
     * Store name (branch name)
     */
    protected storeName?: string;

    /**
     * Add to collection status
     */
    protected buttonStatus = new Status();

    /**
     * Services
     */
    private dfModalService = inject(DfModalService);
    private branchService = inject(BranchService);

    /**
     * Constructor
     */
    constructor() {
        super();
        effect(() => {
            const branchNumber = this.branchService.branchNumber();
            if (this.branchNumber !== branchNumber) {
                this.branchNumber = branchNumber;
                this.updateData();
            }
        });
    }

    /**
     * Update on product change
     */
    override ngOnChanges(changes) {
        super.ngOnChanges(changes);
        this.updateData();
    }

    protected get isAvailble(): boolean {
        if (!this.stockQty) {
            return false;
        }
        return this.qty ? this.qty <= this.stockQty : true;
    }

    /**
     * Open branch selection modal
     */
    protected async openBranchSelection(submitOnBranchSelection?: boolean) {
        const data: NCmsBranchCollection.IModalData = {
            product: this.product!
        };
        this.dfModalService.open(this.buttonStatus, NCmsBranchCollection.MODAL_ID, undefined, data, modalRef => {
            // if we aim to submit after branch is set we need to add callback on modal closed
            if (submitOnBranchSelection) {
                modalRef?.afterClosed().subscribe(async () => {
                    await this.updateData();
                    if (this.state === 'instock') {
                        this.submit();
                    }
                });
            }
        });
    }

    /**
     * If branch is not selected open modal,
     * if can add then submit
     */
    protected submit() {
        switch (this.state) {
            case 'nobranch':
                return this.openBranchSelection(true);
            case 'instock':
                return this.onsubmit.emit({ status: this.buttonStatus, branchNumber: this.branchNumber! });
        }
    }

    /**
     * Sync with branch and product
     */
    private async updateData() {
        if (!this.branchService.isAvailbleForCollection(this.product)) {
            this.setAvailable(false);
            return this.setState('none');
        }
        const branchNumber = this.branchNumber;
        if (!branchNumber) {
            this.setAvailable('noselection');
            return this.setState('nobranch');
        }
        const sku = this.product.data!.sku!;

        const isCurrent = (): boolean => {
            return this.branchNumber === branchNumber && sku === this.product.data!.sku;
        };
        this.stockQty = undefined;
        this.storeName = undefined;
        this.setState('busy');

        if (branchNumber) {
            try {
                await this.branchService.load();
                const stockQty = sku ? await this.branchService.retrieve(sku, branchNumber!) : 0;
                if (isCurrent()) {
                    this.stockQty = stockQty;
                }
                this.storeName = this.branchService.toName(branchNumber);
            } catch (err) {
                if (isCurrent()) {
                    this.error = err instanceof Error ? err.message : err!.toString();
                    return this.setState('error');
                }
            }
        }
        if (!this.stockQty) {
            this.setState('outofstock');
        } else {
            this.setState(this.qty <= this.stockQty ? 'instock' : 'limitedstock');
        }
        this.setAvailable(this.state === 'instock');
    }

    /**
     * Component state setter
     */
    private setState(state: TState) {
        if (this.state !== state) {
            this.state = state;
            this.detectChanges();
        }
    }

    private setAvailable(available: boolean | 'noselection') {
        if (this.available.value !== available) {
            this.available.next(available);
        }
    }

    get stateColor() {
        switch (this.state) {
            case 'error':
                return 'col-2';
            case 'outofstock':
            case 'limitedstock':
            case 'none':
                return 'col-4';
            case 'instock':
                return this.variant === 'basket' ? 'col-3' : 'col-1';
            default:
                return 'col-1';
        }
    }
}
